import { FrontendLogPermissionsPayload } from "@instacover/helpers"
import { useRef } from "react"

import { useGetPresignedUrlV2, useLog } from "@/api/hooks"

import { useGranularCallback, useGranularEffect } from "../../utils/hooks/useGranularHooks"
import { captureException } from "../../utils/monitoring/monitoring"
import { useSessionId } from "../../utils/session"
import { PhotoType, RouterInput } from "../../utils/types"
import { uploadFileToStorage } from "../../utils/upload"

type RemoteLogPayload = RouterInput["app"]["log"]

type CameraLogPayload = Extract<RemoteLogPayload, { type: "CAMERA_V2" }>["report"]

export function useRemoteLogger() {
  const sessionId = useSessionId()

  const getPresignedUrlMutation = useGetPresignedUrlV2()
  const logMutation = useLog()

  const uploadLog = useGranularCallback(
    async (file: File, photoType: PhotoType, message: string, report: Partial<Record<string, unknown>>) => {
      if (!sessionId) {
        return
      }

      try {
        const { presignedUrl: thumbnailUrl, storageRef: debugThumbnailRef } = await getPresignedUrlMutation.mutateAsync(
          { photoType, storagePhotoType: "logs", contentType: file.type },
        )

        logMutation.mutate({ message, type: "UPLOAD", report: { ...report, recordId: debugThumbnailRef } })
        try {
          await uploadFileToStorage(thumbnailUrl, file)
        } catch (error) {
          logMutation.mutate({
            type: "UPLOAD",
            message: `Nepodarilo se nahrat - ${error}`,
            report: { comment: "upload error", recordId: debugThumbnailRef },
          })
        }
      } catch (error) {
        captureException(error)
      }
    },
    [],
    [getPresignedUrlMutation, logMutation],
  )

  const cameraLogV2 = useGranularCallback(
    (payload: CameraLogPayload) => {
      logMutation.mutate({ type: "CAMERA_V2", report: payload })
    },
    [],
    [logMutation],
  )

  const loadLog = useGranularCallback(
    (payload?: Record<string, unknown>) => {
      logMutation.mutate({ message: "Session loaded.", type: "LOADED", report: payload })
    },
    [],
    [logMutation],
  )

  const permissionsLog = useGranularCallback(
    (payload: FrontendLogPermissionsPayload) => {
      logMutation.mutate({
        message: "Permission status",
        type: "PERMISSIONS",
        isoDate: new Date().toISOString(),
        report: payload,
      })
    },
    [],
    [logMutation],
  )

  return { uploadLog, loadLog, cameraLogV2, permissionsLog }
}

type Props = RemoteLogPayload

export function DispatchRemoteLog(props: Props) {
  const { report, isoDate, message, type } = props

  const logMutation = useLog()
  const isDispatchedRef = useRef(false)

  useGranularEffect(
    () => {
      if (logMutation.status !== "idle" || isDispatchedRef.current) {
        return
      }

      isDispatchedRef.current = true
      logMutation.mutate({ report, isoDate, message, type })
    },
    [],
    [logMutation, report, isoDate, message, type],
  )

  return null
}
